import React from "react";
import { reduxForm, Field, InjectedFormProps } from "redux-form";
import { Action } from "typescript-fsa";
import { IntlShape } from "react-intl";

import { ApiRequest } from "@api/requests";
import { ValidateField } from "@validation/models";
import { validate, ValidationRules } from "@validation/index";
import { AsyncForm } from "@api/components/AsyncForm";
import { FormInput } from "@ui/Form/FormInput";
import { FormSelect } from "@ui/Form/FormSelect";
import { Locale } from "@localisation/models";
import { compose } from "@util/compose";

type EntityFieldType = "text" | "email" | "password" | "select";

interface BaseFormField {
    name: string;
    label: string;
    type?: EntityFieldType;
    mapped?: boolean;
    rules?: ValidateField[];
    disabled?: boolean;
}

interface EntityFormFieldText extends BaseFormField {
    type?: "text";
}

interface EntityFormFieldEmail extends BaseFormField {
    type: "email";
}

interface EntityFormFieldPassword extends BaseFormField {
    type: "password";
}

interface EntityFormFieldSelect extends BaseFormField {
    type: "select";
    multiple?: boolean;
    options: any[];
    valueKey?: string;
    labelKey?: string;
}

type EntityFormField = EntityFormFieldText | EntityFormFieldEmail |
    EntityFormFieldPassword | EntityFormFieldSelect;

interface EntityFormProps {
    form: string;
    fields: EntityFormField[];
    submitText: string;
    dispatcher: (data: any) => Action<ApiRequest>;
    className?: string;
    locale: Locale;
    // Redux form props
    validate?: (value: any, props: { intl: IntlShape }) => any;
}

type Props = EntityFormProps & InjectedFormProps<any, EntityFormProps>;

class EntityForm extends React.Component<Props> {
    public render(): JSX.Element {
        const {
            form,
            className,
            submitText,
            handleSubmit,
            dispatcher,
            error,
            locale,
        } = this.props;

        const fields = this.props.fields.map((field: EntityFormField, _index: number) => {
            const props: any = {
                key: form + "-" + field.name,
                id: form + "-" + field.name,
                name: field.name,
                showLabel: true,
                label: field.label,
                disabled: field.disabled,
            };
            if (field.type === "select") {
                props.component = FormSelect;
                props.options = (field as EntityFormFieldSelect).options;
                props.multiple = (field as EntityFormFieldSelect).multiple;
                props.valueKey = (field as EntityFormFieldSelect).valueKey;
                props.labelKey = (field as EntityFormFieldSelect).labelKey;
                props.locale = locale;
            }
            else {
                props.component = FormInput;
                props.type = field.type != null ? field.type : "text";
            }
            return (
                <Field {...props} />
            );
        });

        return (
            <div className={className}>
                <AsyncForm submitText={submitText}
                    handleSubmit={handleSubmit}
                    actionCreator={dispatcher}
                    error={error}>
                    {fields}
                </AsyncForm>
            </div>
        );
    }
}

const addFormValidation = <P extends EntityFormProps>(
    WrappedComponent: React.ComponentClass<P> | React.StatelessComponent<P>,
) => {
    return class ValidatedForm extends React.Component<P & { intl: IntlShape }, {}> {
        public render() {
            // Generate the validate function.
            const rules: ValidationRules = {};
            for (const col of this.props.fields) {
                rules[col.name] = {
                    label: col.label,
                    rules: col.rules ? col.rules : [],
                };
            }
            const validateFunc = validate(rules);

            return (
                <WrappedComponent {...this.props} validate={validateFunc} />
            );
        }
    };
};

const ConnectedEntityForm = compose(
    addFormValidation,
    reduxForm({
        destroyOnUnmount: false,
        enableReinitialize: true,
    }),
)(EntityForm);

export {
    addFormValidation,
    EntityForm as EntityFormPresenter,
    ConnectedEntityForm as EntityForm,
    EntityFormField,
    EntityFormFieldText,
    EntityFormFieldPassword,
    EntityFormFieldSelect,
    EntityFormProps,
};
